/**
 * useLikesAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 8 ("Likes"). Mirrors
 * useFoodPreferencesAnalytics.js exactly — same GA4 wrapper via
 * react-ga4, same IS_PRODUCTION guard, same dev console.group/table
 * logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[LikesAnalytics] ${eventName}`, "color:#2E7D32;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useLikesAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("likes_step_view", { session_id: sessionId, step: 8 });
  }, []);

  const trackLikeAdd = useCallback((sessionId, foodId) => {
    send("likes_food_add", { session_id: sessionId, food: foodId });
  }, []);

  const trackLikeRemove = useCallback((sessionId, foodId) => {
    send("likes_food_remove", { session_id: sessionId, food: foodId });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("likes_step_next", {
      session_id: sessionId,
      step: 8,
      likes: Array.isArray(data.likes) ? data.likes.join(",") : undefined,
      likes_count: Array.isArray(data.likes) ? data.likes.length : undefined,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("likes_step_previous", { session_id: sessionId, step: 8 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("likes_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackLikeAdd,
    trackLikeRemove,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useLikesAnalytics;
